import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { BLACK } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import SectionCard from '../../positionTemplate/components/SectionCard';
import { countStates, formatCurrency } from '../helpers';
import { getPairedLocations, getPairings } from '../workersCompAdapter';

import FieldLabel from './FieldLabel';
import SectionTitle from './SectionTitle';

const valueSx = { color: BLACK[100], fontFamily: PRIMARY_FONT[500], fontSize: '14px' };

const listSx = { display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' };

const dividedItemSx = { borderTop: `1px solid ${BLACK[20]}`, paddingTop: '12px' };

const SummaryItem = ({ label, value }) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <Typography sx={valueSx}>{value}</Typography>
  </Box>
);

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

const PositionSummaryCard = ({ positionTemplate }) => {
  const intl = useIntl();
  const locationCount = getPairings(positionTemplate).length;
  const stateCount = countStates(getPairedLocations(positionTemplate));
  const shiftCount = (positionTemplate.shifts || []).length;

  return (
    <SectionCard
      header={<SectionTitle>{intl.formatMessage({ id: 'pt.detail.summary' })}</SectionTitle>}
    >
      <Box sx={listSx}>
        <SummaryItem
          label={intl.formatMessage({ id: 'pt.detail.summary.corporation' })}
          value={positionTemplate.corporation?.name || '—'}
        />
        <SummaryItem
          label={intl.formatMessage({ id: 'pt.detail.summary.basePayRate' })}
          value={formatCurrency(positionTemplate.payRateReg)}
        />
        <SummaryItem
          label={intl.formatMessage({ id: 'pt.detail.summary.shiftsDefined' })}
          value={intl.formatMessage({ id: 'pt.detail.summary.shiftCount' }, { count: shiftCount })}
        />
        <SummaryItem
          label={intl.formatMessage({ id: 'pt.detail.summary.salesRep' })}
          value={positionTemplate.saleRep || '—'}
        />
        <Box sx={dividedItemSx}>
          <SummaryItem
            label={intl.formatMessage({ id: 'pt.detail.summary.locationsServiced' })}
            value={intl.formatMessage(
              { id: 'pt.detail.summary.locationCount' },
              { locations: locationCount, states: stateCount }
            )}
          />
        </Box>
      </Box>
    </SectionCard>
  );
};

PositionSummaryCard.propTypes = {
  positionTemplate: PropTypes.shape({
    corporation: PropTypes.shape({ name: PropTypes.string }),
    locations: PropTypes.arrayOf(PropTypes.shape({})),
    locationAndWorkersCompPairings: PropTypes.arrayOf(PropTypes.shape({})),
    payRateReg: PropTypes.number,
    saleRep: PropTypes.string,
    shifts: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default PositionSummaryCard;
